/**
 * Organisation Management — real list-detail workspace.
 * Left: paginated organisation list with search/filter.
 * Right: detail header + tabs (Overview, Profile, Users, Access, Subscription, Activity, Danger Zone).
 * Preserves selection in URL for refresh and browser Back.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { useOrganisationDetail, invalidateTenantCache } from '../features/super-admin/hooks/useOrganisationDetail';
import { useOrganisationList as useList } from '../features/super-admin/hooks/useOrganisationList';
import {
  updateTenant,
  updateTenantFeature,
  updateTenantPage,
  deleteTenant,
  getTenantDependencies,
  restoreTenant,
  changeUserTenantRole,
  listRegistrationRequests,
} from '../features/super-admin/api/superAdminApi';
import { LoadingState, ErrorState, EmptyState, StatusBadge } from '../features/super-admin/components/shared';
import { TENANT_ROLE_OPTIONS } from '../features/super-admin/types';
import type { TenantDetail, TenantFeature, TenantPage, TenantUserRow, RegistrationRequest } from '../features/super-admin/types';

const TABS = ['Overview', 'Profile', 'Users', 'Access', 'Subscription', 'Activity', 'Danger Zone'] as const;
type Tab = typeof TABS[number];

const OrganisationManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') ? Number(searchParams.get('id')) : null;
  const tabParam = (searchParams.get('tab') as Tab) || 'Overview';
  const activeTab = TABS.includes(tabParam) ? tabParam : 'Overview';

  const list = useList();
  const { detail, detailLoading, detailError, users, usersLoading, loadDetail, loadUsers, updateDetailCache, clearDetail } = useOrganisationDetail();
  const [selectedTab, setSelectedTab] = useState<Tab>(activeTab);

  // Load detail when selection changes
  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    } else {
      clearDetail();
    }
  }, [selectedId, loadDetail, clearDetail]);

  // Load users when tab changes to Users
  useEffect(() => {
    if (selectedId && selectedTab === 'Users') {
      loadUsers(selectedId);
    }
  }, [selectedId, selectedTab, loadUsers]);

  // Sync tab to URL
  useEffect(() => {
    if (selectedTab !== tabParam) {
      setSearchParams((prev) => {
        prev.set('tab', selectedTab);
        return prev;
      }, { replace: true });
    }
  }, [selectedTab, tabParam, setSearchParams]);

  const selectOrganisation = useCallback((id: number) => {
    setSearchParams({ id: String(id), tab: selectedTab }, { replace: false });
  }, [selectedTab, setSearchParams]);

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-0 border border-slate-200 rounded-lg overflow-hidden bg-white">
      {/* Organisation list */}
      <div className={`${selectedId ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-96 border-r border-slate-200`}>
        <OrgList
          {...list}
          selectedId={selectedId}
          onSelect={selectOrganisation}
        />
      </div>

      {/* Detail panel */}
      <div className={`${selectedId ? 'flex' : 'hidden lg:flex'} flex-col flex-1 min-w-0`}>
        {!selectedId ? (
          <EmptyState
            title="Select an organisation"
            subtitle="Choose from the list to view and manage an organisation."
          />
        ) : detailLoading && !detail ? (
          <LoadingState message="Loading organisation..." />
        ) : detailError && !detail ? (
          <ErrorState message={detailError} onRetry={() => loadDetail(selectedId)} />
        ) : detail ? (
          <>
            <OrgDetailHeader
              detail={detail}
              onBack={() => setSearchParams({}, { replace: true })}
            />
            <OrgTabs activeTab={selectedTab} onTabChange={setSelectedTab} />
            <div className="flex-1 overflow-y-auto">
              {selectedTab === 'Overview' && <OrgOverviewTab detail={detail} />}
              {selectedTab === 'Profile' && <OrgProfileTab detail={detail} />}
              {selectedTab === 'Users' && (
                <OrgUsersTab
                  tenantId={selectedId}
                  users={users}
                  loading={usersLoading}
                  onRefresh={() => loadUsers(selectedId)}
                />
              )}
              {selectedTab === 'Access' && <OrgAccessTab detail={detail} onUpdateCache={updateDetailCache} />}
              {selectedTab === 'Subscription' && <OrgSubscriptionTab detail={detail} />}
              {selectedTab === 'Activity' && <OrgActivityTab detail={detail} />}
              {selectedTab === 'Danger Zone' && <OrgDangerZone detail={detail} />}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

// ─── Organisation List ───────────────────────────────────────────────────────

function OrgList({
  tenants, total, page, loading, error, search, statusFilter,
  setSearch, setStatusFilter, setPage, refresh,
  selectedId, onSelect,
}: {
  tenants: ReturnType<typeof useList>['tenants'];
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
  search: string;
  statusFilter: string;
  setSearch: (s: string) => void;
  setStatusFilter: (s: string) => void;
  setPage: (p: number) => void;
  refresh: () => void;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-slate-200 space-y-2">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organisations..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
            <option value="setup">Setup</option>
          </select>
          <button
            type="button"
            onClick={refresh}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-400">{total.toLocaleString()} organisations</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && <ErrorState message={error} onRetry={refresh} compact />}
        {loading && <LoadingState rows={4} />}
        {!loading && tenants.length === 0 && (
          <EmptyState title="No organisations found" subtitle="Try adjusting your search or filters." />
        )}
        {!loading && tenants.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(t.id)}
            className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
              selectedId === t.id ? 'bg-emerald-50 border-l-2 border-l-emerald-500' : ''
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{t.name}</p>
                <p className="text-xs text-slate-500 truncate">
                  {t.country || ''}{t.slug ? ` · ${t.slug}` : ''}
                </p>
              </div>
              <StatusBadge value={t.status} type="tenant" />
            </div>
            {t.counts && (
              <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                <span>{t.counts.users} users</span>
                <span>{t.counts.farmers} farmers</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-xs text-slate-500">
          <span>Page {page} of {totalPages}</span>
          <div className="flex items-center gap-1">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Detail Header ───────────────────────────────────────────────────────────

function OrgDetailHeader({ detail, onBack }: { detail: TenantDetail; onBack: () => void }) {
  const enabledFeatures = detail.features?.filter((f) => f.is_enabled).length ?? 0;
  const totalFeatures = detail.features?.length ?? 0;
  const enabledPages = detail.pages?.filter((p) => p.is_enabled).length ?? 0;

  return (
    <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="lg:hidden p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          aria-label="Back to list"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700 shrink-0">
          {detail.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-slate-800 truncate">{detail.name}</h2>
            <StatusBadge value={detail.status} type="tenant" />
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span>{detail.slug}</span>
            {detail.country && <span>· {detail.country}</span>}
            {detail.counts && <span>· {detail.counts.users} users</span>}
            <span>· {enabledFeatures}/{totalFeatures} features</span>
            <span>· {enabledPages} pages</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

function OrgTabs({ activeTab, onTabChange }: { activeTab: Tab; onTabChange: (t: Tab) => void }) {
  return (
    <div className="flex items-center gap-0 border-b border-slate-200 overflow-x-auto">
      {TABS.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onTabChange(tab)}
          className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
            activeTab === tab
              ? 'border-emerald-500 text-emerald-700'
              : tab === 'Danger Zone'
                ? 'border-transparent text-red-400 hover:text-red-600 hover:border-red-300'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

// ─── Tab Content: Overview ───────────────────────────────────────────────────

function OrgOverviewTab({ detail }: { detail: TenantDetail }) {
  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricBox label="Users" value={detail.counts?.users ?? 0} color="emerald" />
        <MetricBox label="Farmers" value={detail.counts?.farmers ?? 0} color="blue" />
        <MetricBox label="Cohorts" value={detail.counts?.cohorts ?? 0} color="amber" />
        <MetricBox label="Features" value={detail.features?.filter((f) => f.is_enabled).length ?? 0} color="slate" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoSection title="Contact">
          <InfoRow label="Email" value={detail.contact_email} />
          <InfoRow label="Phone" value={detail.contact_phone} />
          <InfoRow label="Domain" value={detail.domain} />
          <InfoRow label="Admin Domain" value={detail.admin_domain} />
        </InfoSection>
        <InfoSection title="Setup">
          <InfoRow label="Created" value={detail.created_at ? new Date(detail.created_at).toLocaleDateString() : undefined} />
          <InfoRow label="Updated" value={detail.updated_at ? new Date(detail.updated_at).toLocaleDateString() : undefined} />
          <InfoRow label="Marketplace" value={detail.marketplace_access_status || (detail.marketplace_enabled ? 'Enabled (Legacy)' : 'Not Enrolled')} />
          <InfoRow label="Currency" value={detail.default_currency} />
        </InfoSection>
      </div>
    </div>
  );
}

// ─── Tab Content: Profile ────────────────────────────────────────────────────

function OrgProfileTab({ detail }: { detail: TenantDetail }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: detail.name,
    legal_name: detail.legal_name || '',
    country: detail.country || '',
    contact_email: detail.contact_email || '',
    contact_phone: detail.contact_phone || '',
    domain: detail.domain || '',
    admin_domain: detail.admin_domain || '',
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await updateTenant(detail.id, form);
      invalidateTenantCache(detail.id);
      setSaveMsg('Saved');
      setEditing(false);
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err: unknown) {
      setSaveMsg(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Organisation Profile</h3>
        <div className="flex items-center gap-2">
          {saveMsg && <span className="text-xs text-emerald-600">{saveMsg}</span>}
          <button
            type="button"
            onClick={() => setEditing(!editing)}
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(form).map(([key, value]) => (
          <div key={key}>
            <label className="block text-xs font-medium text-slate-500 mb-1 capitalize">
              {key.replace(/_/g, ' ')}
            </label>
            {editing ? (
              <input
                type="text"
                value={value}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              />
            ) : (
              <p className="text-sm text-slate-700 py-1.5">{value || '—'}</p>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Tab Content: Users ──────────────────────────────────────────────────────

function OrgUsersTab({ tenantId, users, loading, onRefresh }: {
  tenantId: number;
  users: TenantUserRow[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [newRole, setNewRole] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleMsg, setRoleMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const startRoleEdit = (u: TenantUserRow) => {
    setEditingUserId(u.id);
    setNewRole(u.role);
    setRoleMsg(null);
  };

  const saveRole = async (u: TenantUserRow) => {
    setRoleSaving(true);
    setRoleMsg(null);
    try {
      await changeUserTenantRole(u.id, tenantId, newRole);
      setRoleMsg({ type: 'success', text: `${u.full_name}'s role updated to ${newRole.replace(/_/g, ' ')}` });
      setEditingUserId(null);
      onRefresh();
    } catch (err: unknown) {
      setRoleMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setRoleSaving(false);
      setTimeout(() => setRoleMsg(null), 3000);
    }
  };

  if (loading) return <LoadingState rows={4} message="Loading users..." />;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">
          Organisation Users ({users.length})
        </h3>
        <button type="button" onClick={onRefresh} className="text-xs text-slate-500 hover:text-slate-700">
          Refresh
        </button>
      </div>

      {roleMsg && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-medium ${roleMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {roleMsg.text}
        </div>
      )}

      {users.length === 0 ? (
        <EmptyState title="No users in this organisation" />
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-medium text-slate-500">Name</th>
                <th className="px-3 py-2 text-xs font-medium text-slate-500">Email</th>
                <th className="px-3 py-2 text-xs font-medium text-slate-500">Role</th>
                <th className="px-3 py-2 text-xs font-medium text-slate-500">Status</th>
                <th className="px-3 py-2 text-xs font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{u.full_name}</td>
                  <td className="px-3 py-2 text-slate-500">{u.email || '—'}</td>
                  <td className="px-3 py-2">
                    {editingUserId === u.id ? (
                      <div className="flex items-center gap-1.5">
                        <select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                        >
                          {TENANT_ROLE_OPTIONS.map((r) => (
                            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => saveRole(u)}
                          disabled={roleSaving || newRole === u.role}
                          className="px-2 py-0.5 text-[11px] font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {roleSaving ? '...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingUserId(null)}
                          className="text-[11px] text-slate-400 hover:text-slate-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <StatusBadge value={u.role} type="role" />
                    )}
                  </td>
                  <td className="px-3 py-2"><StatusBadge value={u.membership_status} type="membership" /></td>
                  <td className="px-3 py-2">
                    {editingUserId !== u.id && (
                      <button
                        type="button"
                        onClick={() => startRoleEdit(u)}
                        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
                      >
                        <PencilIcon className="w-3 h-3" />
                        Role
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab Content: Access ─────────────────────────────────────────────────────

function OrgAccessTab({ detail, onUpdateCache }: { detail: TenantDetail; onUpdateCache: (updater: (prev: TenantDetail) => TenantDetail) => void }) {
  const [saving, setSaving] = useState<string | null>(null);

  const handleFeatureToggle = async (feature: TenantFeature) => {
    setSaving(feature.feature_key);
    try {
      await updateTenantFeature(detail.id, feature.feature_key, !feature.is_enabled);
      onUpdateCache((prev) => ({
        ...prev,
        features: prev.features.map((f) =>
          f.feature_key === feature.feature_key ? { ...f, is_enabled: !f.is_enabled } : f
        ),
      }));
    } catch (err: unknown) {
      console.error('Feature toggle failed:', err);
    } finally {
      setSaving(null);
    }
  };

  const handlePageToggle = async (page: TenantPage) => {
    setSaving(page.page_key);
    try {
      await updateTenantPage(detail.id, page.page_key, !page.is_enabled);
      onUpdateCache((prev) => ({
        ...prev,
        pages: prev.pages.map((p) =>
          p.page_key === page.page_key ? { ...p, is_enabled: !p.is_enabled } : p
        ),
      }));
    } catch (err: unknown) {
      console.error('Page toggle failed:', err);
    } finally {
      setSaving(null);
    }
  };

  const featuresByCategory = (detail.features || []).reduce<Record<string, TenantFeature[]>>((acc, f) => {
    const cat = f.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  return (
    <div className="p-4 space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Feature Modules</h3>
        {Object.entries(featuresByCategory).map(([category, features]) => (
          <div key={category} className="mb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{category}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {features.map((f) => (
                <div key={f.feature_key} className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg">
                  <span className="text-sm text-slate-700">{f.label}</span>
                  <button
                    type="button"
                    disabled={saving === f.feature_key}
                    onClick={() => handleFeatureToggle(f)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      f.is_enabled ? 'bg-emerald-600' : 'bg-slate-300'
                    } ${saving === f.feature_key ? 'opacity-50' : ''}`}
                    title={f.is_enabled ? 'Disable' : 'Enable'}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      f.is_enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Dashboard Pages</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(detail.pages || []).filter((p) => p.page_key !== 'materials').sort((a, b) => a.sort_order - b.sort_order).map((p) => (
            <div key={p.page_key} className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg">
              <span className="text-sm text-slate-700">{p.title}</span>
              <button
                type="button"
                disabled={saving === p.page_key}
                onClick={() => handlePageToggle(p)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  p.is_enabled ? 'bg-emerald-600' : 'bg-slate-300'
                } ${saving === p.page_key ? 'opacity-50' : ''}`}
                title={p.is_enabled ? 'Disable' : 'Enable'}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  p.is_enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab Content: Subscription ───────────────────────────────────────────────

function OrgSubscriptionTab({ detail }: { detail: TenantDetail }) {
  return (
    <div className="p-4 space-y-4">
      <h3 className="text-sm font-semibold text-slate-700">Subscription</h3>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <p className="text-sm text-slate-600">
          Subscription management is currently manual. Contact the organisation to confirm plan details.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <InfoRow label="Marketplace" value={detail.marketplace_access_status || (detail.marketplace_enabled ? 'Enabled (Legacy)' : 'Not Enrolled')} />
          <InfoRow label="Currency" value={detail.default_currency || 'Not set'} />
        </div>
      </div>
    </div>
  );
}

// ─── Tab Content: Activity ───────────────────────────────────────────────────

function OrgActivityTab({ detail }: { detail: TenantDetail }) {
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setRequestsLoading(true);
    listRegistrationRequests()
      .then((data) => {
        if (!cancelled) {
          // Filter to requests that might be related to this tenant
          const filtered = (data.requests || []).filter(
            (r: RegistrationRequest) => r.tenant_id === detail.id ||
              String(r.company_name || '').toLowerCase() === detail.name.toLowerCase()
          );
          setRequests(filtered.slice(0, 10));
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRequestsLoading(false); });
    return () => { cancelled = true; };
  }, [detail.id, detail.name]);

  return (
    <div className="p-4 space-y-4">
      <h3 className="text-sm font-semibold text-slate-700">Recent Activity</h3>

      {/* Registration requests */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
          <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider">Registration Requests</h4>
        </div>
        {requestsLoading ? (
          <LoadingState rows={2} />
        ) : requests.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">
            No registration requests found for this organisation.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {requests.map((r) => (
              <div key={r.id} className="px-3 py-2 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm text-slate-700 truncate">{r.company_name || r.contact_name || `Request #${r.id}`}</p>
                  <p className="text-xs text-slate-400">{r.contact_email || ''} · {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <StatusBadge value={r.status} type="request" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feature/page change hint */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
        <p className="text-xs text-slate-500">
          Organisation-scoped audit logs and platform activity will be displayed here once the audit endpoint supports tenant filtering.
        </p>
      </div>
    </div>
  );
}

// ─── Tab Content: Danger Zone ────────────────────────────────────────────────

function OrgDangerZone({ detail }: { detail: TenantDetail }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Dependency tracking
  const [depsLoading, setDepsLoading] = useState(false);
  const [deps, setDeps] = useState<Record<string, number> | null>(null);
  const [depsLabels, setDepsLabels] = useState<Record<string, string> | null>(null);
  const [depsTotal, setDepsTotal] = useState(0);
  const [depsError, setDepsError] = useState<string | null>(null);

  // Restoration
  const [restoring, setRestoring] = useState(false);

  const isArchived = detail.status === 'archived';

  const loadDependencies = async () => {
    setDepsLoading(true);
    setDepsError(null);
    try {
      const result = await getTenantDependencies(detail.id);
      setDeps(result.linked);
      setDepsLabels(result.linkedLabels || null);
      setDepsTotal(result.linkedTotal);
    } catch (err: unknown) {
      setDepsError(err instanceof Error ? err.message : 'Failed to load dependencies');
    } finally {
      setDepsLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm(`Archive "${detail.name}"? This disables workspace access while retaining records. It can be restored later.`)) return;
    try {
      await deleteTenant(detail.id, false);
      invalidateTenantCache(detail.id);
      setDeleteMsg({ type: 'success', text: 'Organisation archived. Workspace access disabled.' });
    } catch (err: unknown) {
      setDeleteMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to archive' });
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restoreTenant(detail.id);
      invalidateTenantCache(detail.id);
      setDeleteMsg({ type: 'success', text: 'Organisation restored successfully.' });
    } catch (err: unknown) {
      setDeleteMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to restore' });
    } finally {
      setRestoring(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (deleteName !== detail.name) return;
    setDeleting(true);
    setDeleteMsg(null);
    try {
      await deleteTenant(detail.id, true);
      invalidateTenantCache(detail.id);
      setDeleteMsg({ type: 'success', text: 'Organisation permanently deleted.' });
    } catch (err: unknown) {
      // Parse the 409 response to extract dependency counts
      const msg = err instanceof Error ? err.message : 'Failed to delete';
      if (msg.includes('linked data') || msg.includes('409') || msg.includes('Cannot delete')) {
        // Try to reload dependencies to show in the UI
        await loadDependencies();
        setDeleteMsg({ type: 'error', text: 'Deletion blocked. This organisation has linked operational data that cannot be force-deleted. Use Archive instead, or review the dependencies below.' });
      } else {
        setDeleteMsg({ type: 'error', text: msg });
      }
    } finally {
      setDeleting(false);
    }
  };

  const nonZeroDeps = deps ? Object.entries(deps).filter(([, v]) => Number(v) > 0) : [];

  return (
    <div className="p-4 space-y-6">
      {/* Archive / Restore section */}
      <div className={`border rounded-lg p-4 ${isArchived ? 'border-blue-200 bg-blue-50' : 'border-amber-200'}`}>
        <h3 className="text-sm font-semibold text-amber-700">
          {isArchived ? 'Restore Organisation' : 'Archive Organisation'}
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          {isArchived
            ? 'This organisation is archived. Restoring re-enables workspace access and reactivates memberships.'
            : 'Archiving disables workspace access and suspends memberships while retaining all records. This is the recommended lifecycle action.'}
        </p>
        <button
          type="button"
          onClick={isArchived ? handleRestore : handleArchive}
          disabled={restoring}
          className={`mt-3 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            isArchived
              ? 'text-blue-700 border border-blue-300 hover:bg-blue-100 disabled:opacity-50'
              : 'text-amber-700 border border-amber-300 hover:bg-amber-50'
          }`}
        >
          {restoring ? 'Restoring...' : isArchived ? 'Restore' : 'Archive'}
        </button>
      </div>

      {/* Permanent Deletion section */}
      <div className="border border-red-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-red-700">Permanent Deletion</h3>
        <p className="text-xs text-slate-500 mt-1">
          This action is irreversible. The organisation's workspace and all configuration will be permanently removed. Operational data (orders, listings, audit records) may block deletion.
        </p>

        {/* Dependency Summary */}
        {!deps && !depsLoading && (
          <button
            type="button"
            onClick={loadDependencies}
            className="mt-3 px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Check dependencies before deletion
          </button>
        )}

        {depsLoading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <div className="w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
            Loading dependency summary...
          </div>
        )}

        {depsError && (
          <div className="mt-3 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {depsError}
          </div>
        )}

        {deps && nonZeroDeps.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-amber-700 mb-2">
              This organisation owns {depsTotal} linked record{depsTotal !== 1 ? 's' : ''}. Permanent deletion is blocked until these are resolved:
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {nonZeroDeps.map(([key, count]) => (
                <div key={key} className="flex items-center justify-between px-2.5 py-1.5 bg-red-50 rounded-lg text-xs">
                  <span className="text-slate-600">{depsLabels?.[key] || key.replace(/_/g, ' ')}</span>
                  <span className="font-bold text-red-700">{count}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Consider archiving this organisation instead. Archiving disables access while keeping records intact.
            </p>
          </div>
        )}

        {deps && nonZeroDeps.length === 0 && (
          <p className="mt-2 text-xs text-emerald-600 font-medium">
            No linked operational data found. Permanent deletion is allowed.
          </p>
        )}

        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => {
              setConfirmDelete(true);
              if (!deps) loadDependencies();
            }}
            className="mt-3 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
          >
            Delete Permanently
          </button>
        ) : (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-slate-500">
              Type <span className="font-mono font-medium text-red-600">{detail.name}</span> to confirm permanent deletion.
            </p>
            <input
              type="text"
              value={deleteName}
              onChange={(e) => setDeleteName(e.target.value)}
              placeholder={`Type "${detail.name}" to confirm`}
              className="w-full text-sm border border-red-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-500/30"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePermanentDelete}
                disabled={deleteName !== detail.name || deleting}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button
                type="button"
                onClick={() => { setConfirmDelete(false); setDeleteName(''); setDeleteMsg(null); }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {deleteMsg && (
          <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium ${
            deleteMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
          }`}>
            {deleteMsg.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Utility Components ──────────────────────────────────────────────────────

const METRIC_COLORS: Record<string, { bg: string; text: string }> = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700' },
  slate: { bg: 'bg-slate-50', text: 'text-slate-600' },
};

function MetricBox({ label, value, color = 'slate' }: { label: string; value: number; color?: string }) {
  const c = METRIC_COLORS[color] || METRIC_COLORS.slate;
  return (
    <div className={`${c.bg} border border-slate-200 rounded-lg px-3 py-2.5`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${c.text}`}>{value}</p>
    </div>
  );
}

function InfoSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-700 font-medium">{value || '—'}</span>
    </div>
  );
}

export default OrganisationManagementPage;
